import {
    createAsyncThunk,
    createSlice,
} from "@reduxjs/toolkit";

import API
    from "../../utils/apiCalling";

import {
    config,
} from "../../utils/apiUrl";


/*
|--------------------------------------------------------------------------
| API
|--------------------------------------------------------------------------
*/

const api =
    new API();


/*
|--------------------------------------------------------------------------
| INITIAL FILTERS
|--------------------------------------------------------------------------
*/

const INITIAL_FILTERS = {

    page:
        1,

    limit:
        20,

    search:
        "",

    status:
        "",

};


/*
|--------------------------------------------------------------------------
| INITIAL STATE
|--------------------------------------------------------------------------
*/

const initialState = {

    /*
    |--------------------------------------------------------------------------
    | ROLE LIST
    |--------------------------------------------------------------------------
    */

    roles:
        [],


    /*
    |--------------------------------------------------------------------------
    | SELECTED ROLE
    |--------------------------------------------------------------------------
    */

    selectedRole:
        null,


    /*
    |--------------------------------------------------------------------------
    | PERMISSION CATALOG
    |--------------------------------------------------------------------------
    */

    permissionModules:
        [],

    permissions:
        [],


    /*
    |--------------------------------------------------------------------------
    | ROLE DROPDOWN
    |--------------------------------------------------------------------------
    */

    dropdown:
        [],


    /*
    |--------------------------------------------------------------------------
    | PAGINATION
    |--------------------------------------------------------------------------
    */

    pagination: {

        page:
            1,

        limit:
            20,

        totalRecords:
            0,

        totalPages:
            0,

    },


    /*
    |--------------------------------------------------------------------------
    | FILTERS
    |--------------------------------------------------------------------------
    */

    filters: {
        ...INITIAL_FILTERS,
    },


    /*
    |--------------------------------------------------------------------------
    | LOADING
    |--------------------------------------------------------------------------
    */

    listLoading:
        false,

    detailsLoading:
        false,

    permissionLoading:
        false,

    dropdownLoading:
        false,

    createLoading:
        false,

    updateLoading:
        false,

    deleteLoading:
        false,


    /*
    |--------------------------------------------------------------------------
    | ERROR
    |--------------------------------------------------------------------------
    */

    error:
        null,

};


/*
|--------------------------------------------------------------------------
| QUERY STRING
|--------------------------------------------------------------------------
*/

const buildRoleQuery =
    ({
        page = 1,
        limit = 20,
        search = "",
        status = "",
    } = {}) => {

        const params =
            new URLSearchParams();


        params.append(
            "page",
            page
        );


        params.append(
            "limit",
            limit
        );


        if (
            search
        ) {

            params.append(
                "search",
                search
            );

        }


        if (
            status
        ) {

            params.append(
                "status",
                status
            );

        }


        return params
            .toString();

    };


/*
|--------------------------------------------------------------------------
| ERROR
|--------------------------------------------------------------------------
*/

const getErrorPayload =
    (
        error,
        fallback
    ) => {

        return {

            message:
                error
                    ?.response
                    ?.data
                    ?.message ||
                error
                    ?.response
                    ?.message ||
                error
                    ?.message ||
                fallback,

            code:
                error
                    ?.response
                    ?.data
                    ?.code ||
                error
                    ?.response
                    ?.status ||
                null,

            errorCode:
                error
                    ?.response
                    ?.data
                    ?.errorCode ||
                null,

        };

    };


/*
|--------------------------------------------------------------------------
| FETCH ROLES
|--------------------------------------------------------------------------
*/

export const fetchRoles =
    createAsyncThunk(
        "role/fetchRoles",

        async (
            payload = {},
            {
                rejectWithValue,
            }
        ) => {

            try {

                const query =
                    buildRoleQuery(
                        payload
                    );


                const response =
                    await api.get(
                        `${config.getRoles}?${query}`
                    );


                if (
                    response?.success ===
                    false
                ) {

                    return rejectWithValue(
                        response
                    );

                }


                return {

                    roles:
                        Array.isArray(
                            response?.data
                        )
                            ?
                            response.data
                            :
                            [],

                    pagination:
                        response?.pagination ||
                        {},

                };

            } catch (
                error
            ) {

                return rejectWithValue(
                    getErrorPayload(
                        error,
                        "Unable to fetch roles."
                    )
                );

            }

        }
    );


/*
|--------------------------------------------------------------------------
| FETCH ROLE BY ID
|--------------------------------------------------------------------------
*/

export const fetchRoleById =
    createAsyncThunk(
        "role/fetchRoleById",

        async (
            roleId,
            {
                rejectWithValue,
            }
        ) => {

            try {

                const response =
                    await api.get(
                        config.getRoleById(
                            roleId
                        )
                    );


                if (
                    response?.success ===
                    false
                ) {

                    return rejectWithValue(
                        response
                    );

                }


                return (
                    response?.data ||
                    null
                );

            } catch (
                error
            ) {

                return rejectWithValue(
                    getErrorPayload(
                        error,
                        "Unable to fetch role."
                    )
                );

            }

        }
    );


/*
|--------------------------------------------------------------------------
| FETCH PERMISSION CATALOG
|--------------------------------------------------------------------------
|
| IMPORTANT:
|
| Backend should return only permissions belonging to modules available
| in the tenant's current subscription.
|
|--------------------------------------------------------------------------
*/

export const fetchRolePermissionCatalog =
    createAsyncThunk(
        "role/fetchPermissionCatalog",

        async (
            _,
            {
                rejectWithValue,
            }
        ) => {

            try {

                const response =
                    await api.get(
                        config
                            .getRolePermissionCatalog
                    );


                if (
                    response?.success ===
                    false
                ) {

                    return rejectWithValue(
                        response
                    );

                }


                const data =
                    response?.data ||
                    {};


                /*
                |--------------------------------------------------------------------------
                | PREFERRED RESPONSE
                |--------------------------------------------------------------------------
                */

                const modules =
                    Array.isArray(
                        data?.modules
                    )
                        ?
                        data.modules
                        :
                        [];


                const permissions =
                    Array.isArray(
                        data?.permissions
                    )
                        ?
                        data.permissions
                        :
                        [];


                /*
                |--------------------------------------------------------------------------
                | LEGACY GROUPED FALLBACK
                |--------------------------------------------------------------------------
                */

                if (
                    modules.length ===
                    0 &&
                    data?.grouped &&
                    typeof data.grouped ===
                        "object"
                ) {

                    const fallbackModules =
                        Object.entries(
                            data.grouped
                        )
                            .map(
                                ([
                                    moduleCode,
                                    modulePermissions,
                                ]) => ({

                                    code:
                                        moduleCode,

                                    name:
                                        String(
                                            moduleCode
                                        )
                                            .toLowerCase()
                                            .split(
                                                "_"
                                            )
                                            .filter(
                                                Boolean
                                            )
                                            .map(
                                                (
                                                    word
                                                ) =>
                                                    word
                                                        .charAt(
                                                            0
                                                        )
                                                        .toUpperCase() +
                                                    word
                                                        .slice(
                                                            1
                                                        )
                                            )
                                            .join(
                                                " "
                                            ),

                                    permissions:
                                        Array.isArray(
                                            modulePermissions
                                        )
                                            ?
                                            modulePermissions
                                            :
                                            [],

                                })
                            );


                    return {

                        modules:
                            fallbackModules,

                        permissions:
                            fallbackModules
                                .flatMap(
                                    (
                                        module
                                    ) =>
                                        module.permissions
                                ),

                    };

                }


                return {

                    modules,

                    permissions,

                };

            } catch (
                error
            ) {

                return rejectWithValue(
                    getErrorPayload(
                        error,
                        "Unable to fetch role permissions."
                    )
                );

            }

        }
    );


/*
|--------------------------------------------------------------------------
| ROLE DROPDOWN
|--------------------------------------------------------------------------
*/

export const fetchRoleDropdown =
    createAsyncThunk(
        "role/fetchRoleDropdown",

        async (
            {
                includeOwner = false,
            } = {},
            {
                rejectWithValue,
            }
        ) => {

            try {

                const response =
                    await api.get(
                        `${
                            config.getRoleDropdown
                        }?includeOwner=${
                            includeOwner
                                ?
                                "true"
                                :
                                "false"
                        }`
                    );


                if (
                    response?.success ===
                    false
                ) {

                    return rejectWithValue(
                        response
                    );

                }


                return Array.isArray(
                    response?.data
                )
                    ?
                    response.data
                    :
                    [];

            } catch (
                error
            ) {

                return rejectWithValue(
                    getErrorPayload(
                        error,
                        "Unable to fetch role dropdown."
                    )
                );

            }

        }
    );


/*
|--------------------------------------------------------------------------
| CREATE ROLE
|--------------------------------------------------------------------------
*/

export const createRole =
    createAsyncThunk(
        "role/createRole",

        async (
            payload,
            {
                rejectWithValue,
            }
        ) => {

            try {

                const response =
                    await api.post(
                        config.createRole,
                        payload
                    );


                if (
                    response?.success ===
                    false
                ) {

                    return rejectWithValue(
                        response
                    );

                }


                return (
                    response?.data ||
                    null
                );

            } catch (
                error
            ) {

                return rejectWithValue(
                    getErrorPayload(
                        error,
                        "Unable to create role."
                    )
                );

            }

        }
    );


/*
|--------------------------------------------------------------------------
| UPDATE ROLE
|--------------------------------------------------------------------------
*/

export const updateRole =
    createAsyncThunk(
        "role/updateRole",

        async (
            {
                roleId,
                payload,
            },
            {
                rejectWithValue,
            }
        ) => {

            try {

                const response =
                    await api.put(
                        config.updateRole(
                            roleId
                        ),
                        payload
                    );


                if (
                    response?.success ===
                    false
                ) {

                    return rejectWithValue(
                        response
                    );

                }


                return (
                    response?.data ||
                    null
                );

            } catch (
                error
            ) {

                return rejectWithValue(
                    getErrorPayload(
                        error,
                        "Unable to update role."
                    )
                );

            }

        }
    );


/*
|--------------------------------------------------------------------------
| DELETE ROLE
|--------------------------------------------------------------------------
*/

export const deleteRole =
    createAsyncThunk(
        "role/deleteRole",

        async (
            roleId,
            {
                rejectWithValue,
            }
        ) => {

            try {

                const response =
                    await api.delete(
                        config.deleteRole(
                            roleId
                        )
                    );


                if (
                    response?.success ===
                    false
                ) {

                    return rejectWithValue(
                        response
                    );

                }


                return {

                    roleId,

                    response,

                };

            } catch (
                error
            ) {

                return rejectWithValue(
                    getErrorPayload(
                        error,
                        "Unable to delete role."
                    )
                );

            }

        }
    );


/*
|--------------------------------------------------------------------------
| SLICE
|--------------------------------------------------------------------------
*/

const roleSlice =
    createSlice({

        name:
            "role",

        initialState,

        reducers: {

            /*
            |--------------------------------------------------------------------------
            | FILTER
            |--------------------------------------------------------------------------
            */

            setRoleFilter:
                (
                    state,
                    action
                ) => {

                    const {
                        key,
                        value,
                    } =
                        action.payload ||
                        {};


                    if (
                        !key
                    ) {

                        return;

                    }


                    state.filters[
                        key
                    ] =
                        value;

                },


            /*
            |--------------------------------------------------------------------------
            | RESET FILTERS
            |--------------------------------------------------------------------------
            */

            resetRoleFilters:
                (
                    state
                ) => {

                    state.filters = {
                        ...INITIAL_FILTERS,
                    };

                },


            /*
            |--------------------------------------------------------------------------
            | SELECTED ROLE
            |--------------------------------------------------------------------------
            */

            clearSelectedRole:
                (
                    state
                ) => {

                    state.selectedRole =
                        null;

                },


            /*
            |--------------------------------------------------------------------------
            | ERROR
            |--------------------------------------------------------------------------
            */

            clearRoleError:
                (
                    state
                ) => {

                    state.error =
                        null;

                },


            /*
            |--------------------------------------------------------------------------
            | RESET
            |--------------------------------------------------------------------------
            */

            resetRoleState:
                () => ({
                    ...initialState,

                    filters: {
                        ...INITIAL_FILTERS,
                    },
                }),

        },


        /*
        |--------------------------------------------------------------------------
        | EXTRA REDUCERS
        |--------------------------------------------------------------------------
        */

        extraReducers:
            (
                builder
            ) => {

                /*
                |--------------------------------------------------------------------------
                | LIST
                |--------------------------------------------------------------------------
                */

                builder

                    .addCase(
                        fetchRoles
                            .pending,

                        (
                            state
                        ) => {

                            state.listLoading =
                                true;

                            state.error =
                                null;

                        }
                    )


                    .addCase(
                        fetchRoles
                            .fulfilled,

                        (
                            state,
                            action
                        ) => {

                            state.listLoading =
                                false;

                            state.roles =
                                action.payload
                                    ?.roles ||
                                [];

                            state.pagination = {

                                ...state.pagination,

                                ...(
                                    action.payload
                                        ?.pagination ||
                                    {}
                                ),

                            };

                        }
                    )


                    .addCase(
                        fetchRoles
                            .rejected,

                        (
                            state,
                            action
                        ) => {

                            state.listLoading =
                                false;

                            state.error =
                                action.payload ||
                                null;

                        }
                    );


                /*
                |--------------------------------------------------------------------------
                | DETAILS
                |--------------------------------------------------------------------------
                */

                builder

                    .addCase(
                        fetchRoleById
                            .pending,

                        (
                            state
                        ) => {

                            state.detailsLoading =
                                true;

                            state.error =
                                null;

                        }
                    )


                    .addCase(
                        fetchRoleById
                            .fulfilled,

                        (
                            state,
                            action
                        ) => {

                            state.detailsLoading =
                                false;

                            state.selectedRole =
                                action.payload;

                        }
                    )


                    .addCase(
                        fetchRoleById
                            .rejected,

                        (
                            state,
                            action
                        ) => {

                            state.detailsLoading =
                                false;

                            state.error =
                                action.payload ||
                                null;

                        }
                    );


                /*
                |--------------------------------------------------------------------------
                | PERMISSIONS
                |--------------------------------------------------------------------------
                */

                builder

                    .addCase(
                        fetchRolePermissionCatalog
                            .pending,

                        (
                            state
                        ) => {

                            state.permissionLoading =
                                true;

                            state.error =
                                null;

                        }
                    )


                    .addCase(
                        fetchRolePermissionCatalog
                            .fulfilled,

                        (
                            state,
                            action
                        ) => {

                            state.permissionLoading =
                                false;

                            state.permissionModules =
                                action.payload
                                    ?.modules ||
                                [];

                            state.permissions =
                                action.payload
                                    ?.permissions ||
                                [];

                        }
                    )


                    .addCase(
                        fetchRolePermissionCatalog
                            .rejected,

                        (
                            state,
                            action
                        ) => {

                            state.permissionLoading =
                                false;

                            state.permissionModules =
                                [];

                            state.permissions =
                                [];

                            state.error =
                                action.payload ||
                                null;

                        }
                    );


                /*
                |--------------------------------------------------------------------------
                | DROPDOWN
                |--------------------------------------------------------------------------
                */

                builder

                    .addCase(
                        fetchRoleDropdown
                            .pending,

                        (
                            state
                        ) => {

                            state.dropdownLoading =
                                true;

                        }
                    )


                    .addCase(
                        fetchRoleDropdown
                            .fulfilled,

                        (
                            state,
                            action
                        ) => {

                            state.dropdownLoading =
                                false;

                            state.dropdown =
                                action.payload ||
                                [];

                        }
                    )


                    .addCase(
                        fetchRoleDropdown
                            .rejected,

                        (
                            state,
                            action
                        ) => {

                            state.dropdownLoading =
                                false;

                            state.error =
                                action.payload ||
                                null;

                        }
                    );


                /*
                |--------------------------------------------------------------------------
                | CREATE
                |--------------------------------------------------------------------------
                */

                builder

                    .addCase(
                        createRole
                            .pending,

                        (
                            state
                        ) => {

                            state.createLoading =
                                true;

                            state.error =
                                null;

                        }
                    )


                    .addCase(
                        createRole
                            .fulfilled,

                        (
                            state,
                            action
                        ) => {

                            state.createLoading =
                                false;


                            if (
                                action.payload
                            ) {

                                state.roles = [

                                    action.payload,

                                    ...state.roles,

                                ];

                            }

                        }
                    )


                    .addCase(
                        createRole
                            .rejected,

                        (
                            state,
                            action
                        ) => {

                            state.createLoading =
                                false;

                            state.error =
                                action.payload ||
                                null;

                        }
                    );


                /*
                |--------------------------------------------------------------------------
                | UPDATE
                |--------------------------------------------------------------------------
                */

                builder

                    .addCase(
                        updateRole
                            .pending,

                        (
                            state
                        ) => {

                            state.updateLoading =
                                true;

                            state.error =
                                null;

                        }
                    )


                    .addCase(
                        updateRole
                            .fulfilled,

                        (
                            state,
                            action
                        ) => {

                            state.updateLoading =
                                false;


                            const updatedRole =
                                action.payload;


                            if (
                                !updatedRole
                            ) {

                                return;

                            }


                            state.roles =
                                state.roles
                                    .map(
                                        (
                                            role
                                        ) =>
                                            String(
                                                role?._id
                                            ) ===
                                            String(
                                                updatedRole?._id
                                            )
                                                ?
                                                updatedRole
                                                :
                                                role
                                    );


                            if (
                                String(
                                    state
                                        .selectedRole
                                        ?._id ||
                                    ""
                                ) ===
                                String(
                                    updatedRole?._id
                                )
                            ) {

                                state.selectedRole =
                                    updatedRole;

                            }

                        }
                    )


                    .addCase(
                        updateRole
                            .rejected,

                        (
                            state,
                            action
                        ) => {

                            state.updateLoading =
                                false;

                            state.error =
                                action.payload ||
                                null;

                        }
                    );


                /*
                |--------------------------------------------------------------------------
                | DELETE
                |--------------------------------------------------------------------------
                */

                builder

                    .addCase(
                        deleteRole
                            .pending,

                        (
                            state
                        ) => {

                            state.deleteLoading =
                                true;

                            state.error =
                                null;

                        }
                    )


                    .addCase(
                        deleteRole
                            .fulfilled,

                        (
                            state,
                            action
                        ) => {

                            state.deleteLoading =
                                false;


                            const roleId =
                                action.payload
                                    ?.roleId;


                            state.roles =
                                state.roles
                                    .filter(
                                        (
                                            role
                                        ) =>
                                            String(
                                                role?._id
                                            ) !==
                                            String(
                                                roleId
                                            )
                                    );


                            if (
                                String(
                                    state
                                        .selectedRole
                                        ?._id ||
                                    ""
                                ) ===
                                String(
                                    roleId
                                )
                            ) {

                                state.selectedRole =
                                    null;

                            }

                        }
                    )


                    .addCase(
                        deleteRole
                            .rejected,

                        (
                            state,
                            action
                        ) => {

                            state.deleteLoading =
                                false;

                            state.error =
                                action.payload ||
                                null;

                        }
                    );

            },

    });


/*
|--------------------------------------------------------------------------
| ACTIONS
|--------------------------------------------------------------------------
*/

export const {

    setRoleFilter,

    resetRoleFilters,

    clearSelectedRole,

    clearRoleError,

    resetRoleState,

} =
    roleSlice.actions;


/*
|--------------------------------------------------------------------------
| SELECTORS
|--------------------------------------------------------------------------
*/

export const selectRoles =
    (
        state
    ) =>
        state
            .role
            ?.roles ||
        [];


export const selectSelectedRole =
    (
        state
    ) =>
        state
            .role
            ?.selectedRole ||
        null;


export const selectRolePermissionModules =
    (
        state
    ) =>
        state
            .role
            ?.permissionModules ||
        [];


export const selectRolePermissions =
    (
        state
    ) =>
        state
            .role
            ?.permissions ||
        [];


export const selectRoleDropdown =
    (
        state
    ) =>
        state
            .role
            ?.dropdown ||
        [];


export const selectRolePagination =
    (
        state
    ) =>
        state
            .role
            ?.pagination ||
        initialState.pagination;


export const selectRoleFilters =
    (
        state
    ) =>
        state
            .role
            ?.filters ||
        INITIAL_FILTERS;


export const selectRoleListLoading =
    (
        state
    ) =>
        Boolean(
            state
                .role
                ?.listLoading
        );


export const selectRoleDetailsLoading =
    (
        state
    ) =>
        Boolean(
            state
                .role
                ?.detailsLoading
        );


export const selectRolePermissionLoading =
    (
        state
    ) =>
        Boolean(
            state
                .role
                ?.permissionLoading
        );


export const selectRoleCreateLoading =
    (
        state
    ) =>
        Boolean(
            state
                .role
                ?.createLoading
        );


export const selectRoleUpdateLoading =
    (
        state
    ) =>
        Boolean(
            state
                .role
                ?.updateLoading
        );


export const selectRoleDeleteLoading =
    (
        state
    ) =>
        Boolean(
            state
                .role
                ?.deleteLoading
        );


export const selectRoleSaving =
    (
        state
    ) =>
        Boolean(
            state
                .role
                ?.createLoading ||
            state
                .role
                ?.updateLoading
        );


export const selectRoleError =
    (
        state
    ) =>
        state
            .role
            ?.error ||
        null;


/*
|--------------------------------------------------------------------------
| EXPORT
|--------------------------------------------------------------------------
*/

export default
    roleSlice.reducer;